import { useState } from "react";
import styled from "@emotion/styled";
import { GrCheckbox, GrIcon, GrStack, GrTypography, palette } from "@jnpmedi/grebob";
import { formatSignerNameWithIndex } from "@newComponents/myDocument/createESignSidebar/converting";
import { langHelper, MvnMessage } from "@newLibs/utils";
import { useDocumentContext } from "@newHooks/context/document/document.context";
import { DocumentMarker, useDocumentMarkerContext } from "@newHooks/context/document/documentMarker.context";
import { useESignSignerContext } from "@newHooks/context/eSign/eSignSigner.context";
import { multiLineEllipsis } from "@assets/styles/layout";
import { SignatureResult, StyledMarker, StyledMarkerZone } from "./fileDetailViewerPageMarkerZone.styled";
import { DocumentSignerSignatureExt, getFieldTypeWithAttr } from "../types";
import { getSignatureImageBase64 } from "../Viewer.function";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs";
import { testKey } from "@maven-rest/automationTest/e2e/docs/testKey";
import { DocumentSignerAttrType, DocumentSignType } from "@maven-rest/docs/DocumentSign.dto";
import dayjs from "dayjs";
import { uniq } from "lodash";
import { v4 as uuidv4 } from "uuid";

export function FileDetailViewerPageMarkerZone({
  page,
  zoom,
  width,
  height,
  onMarkerClicked,
  signatures,
  signType,
}: {
  page: number;
  zoom: number;
  width: number;
  height: number;
  onMarkerClicked?: (signerUuid: string, attrUuid: string, attr: DocumentSignerAttrType) => void;
  signatures?: DocumentSignerSignatureExt[];
  signType?: DocumentSignType;
}) {
  const lang = langHelper.getLang();
  const { mode, setMode, programmatically } = useDocumentContext();
  const { markers, setMarkers } = useDocumentMarkerContext();
  const { signers } = useESignSignerContext();

  const [currentLinkingAttr, setCurrentLinkingAttr] = useState<{ uuid: string; attr: DocumentSignerAttrType }>();

  const pageIndex = page - 1;

  const link = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>, page: number) => {
    if (typeof mode !== "object" || mode.type !== "LINKING") return;

    let currentTargetRect = ev.currentTarget.getBoundingClientRect();
    const offsetX = ev.pageX - currentTargetRect.left;
    const offsetY = ev.pageY - window.pageYOffset - currentTargetRect.top;
    const width = currentTargetRect.width;
    const height = currentTargetRect.height;
    const x = Math.min(99, Math.max(0, (offsetX / width) * 100));
    const y = Math.min(99, Math.max(0, (offsetY / height) * 100));

    setMarkers(
      markers
        .filter((x) => !(mode.groupUuid === x.groupUuid && x.attrUuid === mode.attrUuid && x.attr === mode.attr))
        .concat([{ ...mode, x, y, page, groupUuid: mode.groupUuid, attrUuid: mode.attrUuid }])
    );
    setCurrentLinkingAttr({ uuid: mode.signerUuid, attr: mode.attr });
  };

  const endLink = () => {
    if (typeof mode !== "object" || mode.type !== "LINKING") return;

    setCurrentLinkingAttr(undefined);
    setMode("SIGNER");
  };

  const unlink = (
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    uuid: string,
    attr: DocumentSignerAttrType,
    groupUuid: string,
    attrUuid: string
  ) => {
    e.stopPropagation();

    setMarkers(
      markers.filter(
        (marker) => !(marker.signerUuid === uuid && marker.attr === attr && marker.groupUuid === groupUuid && marker.attrUuid === attrUuid)
      )
    );
  };

  const markerLabel = (marker: DocumentMarker) => {
    if (marker.attr === DocumentSignerAttrType.ResponsiveItemFieldMark) {
      return "Check";
    } else if (marker.attr === DocumentSignerAttrType.StampApiMark) {
      return "Stamp";
    }

    return (
      <>
        {(() => {
          const { attr } = marker;
          if ([DocumentSignerAttrType.Signature, DocumentSignerAttrType.AgentSignature, DocumentSignerAttrType.Sign].includes(attr)) {
            return "Sign";
          }

          const fieldType = getFieldTypeWithAttr(attr);
          if (fieldType) return fieldType;

          return attr.charAt(0) + attr.slice(1).toLowerCase();
        })()}
      </>
    );
  };

  const getIsCheckedResponsiveItem = (marker: DocumentMarker) =>
    !!markers.find(
      (e) => e.groupUuid === marker.groupUuid && e.attr === DocumentSignerAttrType.ResponsiveItemValue && e.value === marker.attrUuid
    );

  const getReadableSignerIndex = (signerIndex: number) =>
    ((v) => v.indexOf(signerIndex) + 1)(uniq(markers.map((e) => e.signerIndex).sort((a, b) => a - b)));

  const markersFilterCriteria = (marker: DocumentMarker) =>
    marker.page === pageIndex && marker.x !== null && marker.y !== null && marker.attr !== DocumentSignerAttrType.ResponsiveItemValue;

  const handleMouseMoveMarkerZone = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
    !!markers.find((marker) => marker.signerUuid === currentLinkingAttr?.uuid && marker.attr === currentLinkingAttr?.attr) &&
    link(ev, pageIndex);

  const handleClickMarker = (marker: DocumentMarker) => {
    if (marker.attr === DocumentSignerAttrType.ResponsiveItemFieldMark) {
      const parentGroupUuid = markers.find(
        (e) => e.attrUuid === marker.groupUuid && e.attr === DocumentSignerAttrType.ResponsiveItemFieldIndex
      )?.groupUuid;

      const answersCount = ((groupUuid) =>
        markers.filter((e) => e.groupUuid === groupUuid && e.attr === DocumentSignerAttrType.ResponsiveItemFieldIndex))(
        markers.find((e) => e.attrUuid === marker.groupUuid && e.attr === DocumentSignerAttrType.ResponsiveItemFieldIndex)?.groupUuid
      ).filter((e) =>
        markers.find((m) => m.groupUuid === e.attrUuid && m.attr === DocumentSignerAttrType.ResponsiveItemFieldRequired)
      ).length;

      const exist = !!markers.find((x) => x.groupUuid === marker.groupUuid && x.attr === DocumentSignerAttrType.ResponsiveItemValue);

      setMarkers(
        markers
          .filter((x) => !(x.groupUuid === marker.groupUuid && x.attr === DocumentSignerAttrType.ResponsiveItemValue))
          .filter(
            (x) =>
              !(
                signType !== DocumentSignType.EConsent &&
                answersCount === 1 &&
                markers.find((e) => e.attrUuid === x.groupUuid && DocumentSignerAttrType.ResponsiveItemFieldIndex)?.groupUuid ===
                  parentGroupUuid &&
                x.attr === DocumentSignerAttrType.ResponsiveItemValue
              )
          )
          .concat(
            exist
              ? []
              : [
                  {
                    id: marker.id,
                    signerUuid: marker.signerUuid,
                    target: marker.target,
                    signerIndex: marker.signerIndex,
                    attr: DocumentSignerAttrType.ResponsiveItemValue,
                    attrUuid: uuidv4(),
                    groupUuid: marker.groupUuid,
                    value: marker.attrUuid,
                    page: marker.page,
                    x: marker.x,
                    y: marker.y,
                  },
                ]
          )
      );
    }

    onMarkerClicked?.(marker.signerUuid, marker.attrUuid, marker.attr);
  };

  return (
    <StyledMarkerZone
      width={`calc(${width}px - 2px)`}
      height={`calc(${height}px - 2px)`}
      style={{ boxSizing: "border-box" }}
      transform={`scale(${zoom})`}
      transformOrigin="top left"
      onMouseDown={(ev) => link(ev, pageIndex)}
      onMouseMove={handleMouseMoveMarkerZone}
      onMouseUp={() => endLink()}
      isVisible
      isModeLinking={typeof mode !== "string" && "type" in mode && mode.type === "LINKING"}
    >
      {markers.filter(markersFilterCriteria).map((marker) => {
        const matchedSigner = signers.find((s) => s.uuid === marker.signerUuid);
        const signature =
          marker.attr === DocumentSignerAttrType.Signature ? signatures?.find((e) => e.attrUuid === marker.attrUuid) : undefined;

        return (
          <StyledMarker
            className={resolveTestKeyBySignerAttrType(marker.attr)}
            id={marker.id ? `mdx--pdf-marker--id-${marker.id}` : `mdx--pdf-marker--uuid-${marker.groupUuid}-${marker.attrUuid}`}
            key={`${marker.signerUuid}-${marker.attr}-${marker.groupUuid || "NONE"}-${marker.attrUuid || "NONE"}`}
            lang={lang}
            signerIndex={marker.signerIndex}
            markerColor={matchedSigner?.signerColor}
            signerAttrType={marker.attr}
            left={`${marker.x}%`}
            top={`${marker.y}%`}
            isModeLinking={typeof mode !== "string" && "type" in mode && mode.type === "LINKING"}
            isModeSigning={mode === "SIGNING"}
            isChecked={getIsCheckedResponsiveItem(marker)}
            isSigned={!!signature?.signature}
            signature={signature}
            isReadonly={programmatically}
            onClick={() => handleClickMarker(marker)}
            data-label={MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_SIGNATURE_ANIMATION_DESC)}
          >
            {mode === "SIGNING" &&
              (() => {
                if (marker.attr === DocumentSignerAttrType.Name) return marker.value || "Name";
                if (marker.attr === DocumentSignerAttrType.Sign) return MvnMessage(COMMON_LANG.SIGN_ALTER);
                if (marker.attr === DocumentSignerAttrType.Signature) return MvnMessage(COMMON_LANG.SIGN_ALTER);
                if (marker.attr === DocumentSignerAttrType.StampApiMark) return "Automatic";
                if (marker.attr === DocumentSignerAttrType.Date) {
                  return String(markers.find((e) => e.attr === DocumentSignerAttrType.DateFormat)?.value || "YYYY-MM-DD")
                    .split("Z")
                    .join(dayjs().format("Z"));
                }
                if (marker.attr === DocumentSignerAttrType.ResponsiveItemFieldMark)
                  return <GrCheckbox checked={getIsCheckedResponsiveItem(marker)} size="large" />;

                return <>{marker.value || marker.attr}</>;
              })()}
            {mode !== "SIGNING" && (
              <>
                <GrStack className="marker-label-wrapper" alignItems="center">
                  <GrTypography type="Content.Body04" style={{ userSelect: "none" }} css={multiLineEllipsis(1)}>
                    {markerLabel(marker)}&nbsp;
                    <GrTypography type="Content.Caption04" style={{ userSelect: "none" }}>
                      {marker.target === "SIGNER" &&
                        `(${formatSignerNameWithIndex({
                          name: matchedSigner?.name,
                          signerIndex: getReadableSignerIndex(matchedSigner?.signerIndex || marker.signerIndex),
                        })})`}
                    </GrTypography>
                  </GrTypography>
                </GrStack>
                {!programmatically && (
                  <span
                    className="notranslate close-icon"
                    onClick={(e) => unlink(e, marker.signerUuid, marker.attr, marker.groupUuid, marker.attrUuid)}
                  >
                    <CloseIcon type="circle-x-filled" size={20} />
                  </span>
                )}
              </>
            )}
            {marker.attr === DocumentSignerAttrType.Signature && !!signatures?.find((e) => e.attrUuid === marker.attrUuid) && (
              <SignatureResult
                width={signature?.width}
                height={signature?.height}
                url={getSignatureImageBase64(signatures?.find((e) => e.attrUuid === marker.attrUuid))}
              />
            )}
          </StyledMarker>
        );
      })}
    </StyledMarkerZone>
  );
}

const resolveTestKeyBySignerAttrType = (attrType: DocumentSignerAttrType): string | undefined => {
  if (attrType === DocumentSignerAttrType.Signature) {
    return testKey.eSign.signatureMarker;
  }
};

const CloseIcon = styled(GrIcon)`
  & > svg {
    background-color: ${palette["white"]};
    border-radius: 50%;
  }
`;
